import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ReferenceLine,
} from "recharts";

const palette = [
  "#5B8DEF", // blue
  "#34C38F", // green
  "#F6C343", // yellow
  "#EF6A6A", // red
  "#A78BFA", // purple
  "#22D3EE", // cyan
  "#F59E0B", // amber
  "#EC4899", // pink
];

// Compact number formatting with currency symbol (no unnecessary decimals, minus before $)
const fmt = (n) => {
  if (n === null || n === undefined) return "";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(0)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(0)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs}`;
};



function useChartData(preview) {
  return useMemo(() => {
    if (!preview) return { rows: [], keys: [], splitX: null };
    const { labels = [], keys = [], series = {}, splitIndex = null } = preview;

    const rows = labels.map((period, i) => {
      const row = { period, _i: i, _isForecast: splitIndex !== null && i >= splitIndex };
      keys.forEach((k) => {
        const v = Array.isArray(series[k]) ? series[k][i] : null;
        row[k] = typeof v === "number" ? v : null;
      });
      return row;
    });

    const splitX = splitIndex !== null && splitIndex >= 0 && splitIndex < labels.length
      ? labels[splitIndex]
      : null;

    return { rows, keys, splitX };
  }, [preview]);
}
// --- Mobile helpers: treat <=640px as mobile ---
const isMobileViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(max-width: 640px)").matches;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const isForecast = payload?.[0]?.payload?._isForecast;
  return (
    <div style={{
      background: "#111827",
      color: "white",
      borderRadius: 8,
      padding: "10px 12px",
      boxShadow: "0 8px 22px rgba(0,0,0,.35)",
      border: "1px solid #1f2937"
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {payload.map((p, idx) => (
        <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, lineHeight: "18px" }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: "inline-block" }} />
          <span style={{ opacity: 0.85 }}>{p.name}:</span>
          <strong>{fmt(p.value)}</strong>
        </div>
      ))}
      <div style={{ marginTop: 6, fontSize: 11, opacity: 0.8 }}>
        {isForecast ? "Forecast" : "Actuals"}
      </div>
    </div>
  );
};

export default function ForecastChart({ preview }) {
  const { rows, keys, splitX } = useChartData(preview);

  const mobile = isMobileViewport();
  // On mobile, only show the last 3 labeled periods to keep the chart readable.
  const displayedRows = useMemo(() => {
    if (!mobile) return rows;
    if (!Array.isArray(rows) || rows.length <= 3) return rows;
    return rows.slice(-3);
  }, [rows, mobile]);

  // If the forecast split marker is outside of the displayed window, hide it.
  const displayedSplitX = useMemo(() => {
    if (!mobile) return splitX;
    const set = new Set(displayedRows.map(r => r.period));
    return set.has(splitX) ? splitX : null;
  }, [mobile, displayedRows, splitX]);

  // If we have 1–2 metrics, use Areas; otherwise Lines to reduce clutter.
  const useAreas = keys.length <= 2;

  return (
    <div className="chart-card">
      <ResponsiveContainer width="100%" height={mobile ? 220 : 360}>
        <ComposedChart data={displayedRows} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>

          <defs>
            {keys.map((k, i) => (
              <linearGradient key={k} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="10%" stopColor={palette[i % palette.length]} stopOpacity={0.35} />
                <stop offset="95%" stopColor={palette[i % palette.length]} stopOpacity={0.05} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="period"
            tick={mobile ? { fontSize: 11 } : { fontSize: 12, angle: -15, textAnchor: 'end' }}
            height={mobile ? 28 : 40}
          />
          <YAxis tickFormatter={fmt} tick={{ fontSize: mobile ? 11 : 12 }} width={mobile ? 64 : 78} />
          <Tooltip content={<CustomTooltip />} />
          {!mobile && (
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
            />
          )}



          {displayedSplitX && (
            <ReferenceLine
              x={displayedSplitX}
              stroke="#9CA3AF"
              strokeDasharray="6 6"
              label={{ value: "Forecast starts", position: "insideTopRight", fill: "#6B7280", fontSize: 11 }}
            />
          )}

          {keys.map((k, i) =>
            useAreas ? (
              <Area
                key={k}
                type="monotone"
                dataKey={k}
                name={k}
                stroke={palette[i % palette.length]}
                fillOpacity={1}
                fill={`url(#grad-${i})`}
                strokeWidth={i === 0 ? 3 : 1.5}
                strokeOpacity={i === 0 ? 1 : 0.7}
                animationDuration={600}
                isAnimationActive
                connectNulls
              />
            ) : (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                name={k}
                stroke={palette[i % palette.length]}
                dot={false}
                strokeWidth={i === 0 ? 3 : 1.5}
                strokeOpacity={i === 0 ? 1 : 0.7}
                animationDuration={550}
                isAnimationActive
                connectNulls
              />
            )
          )}

          {!mobile && (
            <Brush dataKey="period" height={24} travellerWidth={9} stroke="#9CA3AF" />
          )}

        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
